import { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Typography, GlobalStyles } from '@mui/material';
import { ApiError, logoutRequest } from './lib/api';
import { wsClient } from './lib/wsClient';
import { iconCache } from './lib/iconCache';
import { useAuthStore } from './lib/auth';
import { useSidebarStore } from './store/sidebarStore';
import { useWindowStore, makeWindowKey } from './store/windowStore';
import { useAutoActionsStore } from './store/autoActionsStore';
import { fetchLayoutPreferences, touchAgent } from './lib/layoutPrefs';
import { openPanel, refreshLayoutCache } from './lib/windowManager';
import { RequireAuth } from './components/RequireAuth';
import { AppShell } from './components/shell/AppShell';
import { AgentSidebar } from './components/sidebar/AgentSidebar';
import { AgentDashboard } from './components/dashboard/AgentDashboard';
import { WindowManager } from './components/floating/WindowManager';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PermissionsPage } from './pages/admin/PermissionsPage';
import { WatchedPathsPage } from './pages/admin/WatchedPathsPage';
import { UpdatesWindow } from './components/admin/UpdatesWindow';
import { MyProfileModal } from './components/modals/MyProfileModal';
import { PermissionsModal } from './components/modals/PermissionsModal';
import { WatchedPathsModal } from './components/modals/WatchedPathsModal';
import { AgentEventsPage } from './pages/AgentEventsPage';
import { AgentDevicesPage } from './pages/AgentDevicesPage';
import AgentUrlsPage from './pages/AgentUrlsPage';
import { DashboardPage } from './pages/DashboardPage';
import { WallboardPage } from './pages/WallboardPage';
import { DeviceEventsToast } from './components/DeviceEventsToast';
import { AgentStatusToast } from './components/AgentStatusToast';
import { AdminAlertToast } from './components/AdminAlertToast';
import { getArgusTheme, type ArgusThemeMode } from './theme/argusTheme';
import './lib/dayjsSetup';

const THEME_MODE_STORAGE_KEY = 'argus.ui.themeMode';

const argusGlobalStyles = (
  <GlobalStyles
    styles={(theme) => {
      const isDark = theme.palette.mode === 'dark';
      const thumb = isDark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.22)';
      const thumbHover = isDark ? 'rgba(255, 255, 255, 0.32)' : 'rgba(0, 0, 0, 0.36)';
      return {
        '@keyframes argusPulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
        // App-level: dezactivează selectarea text + meniu contextual nativ browser.
        'html, body, #root': {
          userSelect: 'none',
          WebkitUserSelect: 'none',
          msUserSelect: 'none',
          MozUserSelect: 'none',
        },
        'input, textarea, [data-allow-select="true"]': {
          userSelect: 'text !important',
          WebkitUserSelect: 'text !important',
        },
        // Scrollbars slim + theme-aware (Chromium/Edge/Safari WebKit).
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: thumb,
          borderRadius: 4,
          '&:hover': { backgroundColor: thumbHover },
        },
        '*::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
        // Firefox.
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${thumb} transparent`,
        },
      };
    }}
  />
);

export function App(): JSX.Element {
  const [themeMode, setThemeMode] = useState<ArgusThemeMode>(() => {
    const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
    return (stored === 'light' || stored === 'dark') ? stored : 'dark';
  });

  useEffect(() => {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, themeMode);
  }, [themeMode]);

  // Disable browser native context menu app-level. Excepție: elemente cu data-allow-context-menu.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-allow-context-menu="true"]')) return;
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, []);

  const toggleTheme = () => setThemeMode((m) => (m === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeProvider theme={getArgusTheme(themeMode)}>
      <CssBaseline />
      {argusGlobalStyles}
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <AuthenticatedShell themeMode={themeMode} onToggleTheme={toggleTheme} />
            </RequireAuth>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

interface AuthenticatedShellProps {
  themeMode: ArgusThemeMode;
  onToggleTheme: () => void;
}

function AuthenticatedShell({ themeMode, onToggleTheme }: AuthenticatedShellProps): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const selectedAgentId = useSidebarStore((s) => s.selectedAgentId);
  const setSelectedAgentId = useSidebarStore((s) => s.setSelectedAgentId);
  const hydrateWindows = useWindowStore((s) => s.hydrate);
  const clearWindows = useWindowStore((s) => s.clear);
  const [loggingOut, setLoggingOut] = useState(false);

  // Eager WS connect post-auth — pentru a primi push-uri server (USB events real-time, etc.).
  // Bloc continuous-monitoring-usb 2026-05-06.
  useEffect(() => {
    void wsClient.connectEager();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchLayoutPreferences()
      .then((items) => {
        if (cancelled) return;
        // Update cache pentru reopen restore (chiar și pentru rows cu isOpen=false).
        refreshLayoutCache(items);
        const restored = items
          .filter((p) => p.isOpen)
          .map((p) => ({
            key: makeWindowKey(p.agentId, p.panelType),
            agentId: p.agentId,
            panelType: p.panelType,
            x: p.x,
            y: p.y,
            width: p.width,
            height: p.height,
            zOrder: p.zOrder,
            isMaximized: p.isMaximized,
            isMinimized: p.isMinimized,
          }));
        hydrateWindows(restored);
      })
      .catch((err) => {
        console.warn('Layout fetch failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [hydrateWindows]);

  async function handleLogout(): Promise<void> {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logoutRequest();
    } catch (err) {
      if (!(err instanceof ApiError)) {
        throw err;
      }
    } finally {
      wsClient.close();
      iconCache.clearAll();
      clearWindows();
      clearAuth();
      navigate('/login', { replace: true });
    }
  }

  function handleSelectAgent(agentId: string): void {
    setSelectedAgentId(agentId);
    void touchAgent(agentId).catch((err) => {
      console.warn('Touch failed', agentId, err);
    });
  }

  if (user === null) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell
      themeMode={themeMode}
      onToggleTheme={onToggleTheme}
      userName={user.username}
      userRole={user.role}
      onLogout={() => void handleLogout()}
      sidebar={
        <AgentSidebar
          onAgentSelect={handleSelectAgent}
          onQuickScreenshot={(agent) => {
            // Quick Screenshot — open Desktop panel + auto-trigger captureScreenshot WS direct.
            // Existing infra: wsClient.requestScreenshot returns full virtual screen PNG (toate
            // monitoarele combinate într-un singur image). Resize NU se face — dimensiune nativă.
            handleSelectAgent(agent.id);
            useAutoActionsStore.getState().setAutoAction(`${agent.id}:desktop`, 'screenshot');
            openPanel('desktop', agent.id);
          }}
          onQuickFileExplorer={(agent) => {
            handleSelectAgent(agent.id);
            openPanel('fileExplorer', agent.id);
          }}
          onQuickLivestream={(agent) => {
            // Quick Webcam — open Webcam panel + auto-trigger snap-picture (default cam, max res).
            handleSelectAgent(agent.id);
            useAutoActionsStore.getState().setAutoAction(`${agent.id}:webcam`, 'snap-picture');
            openPanel('webcam', agent.id);
          }}
        />
      }
    >
      <Routes>
        <Route
          path="/"
          element={
            selectedAgentId ? (
              <AgentDashboard key={selectedAgentId} agentId={selectedAgentId} onOpenPanel={openPanel} />
            ) : (
              <EmptyDashboard />
            )
          }
        />
        <Route
          path="/agents"
          element={
            selectedAgentId ? (
              <AgentDashboard key={selectedAgentId} agentId={selectedAgentId} onOpenPanel={openPanel} />
            ) : (
              <EmptyDashboard />
            )
          }
        />
        <Route
          path="/admin/permissions"
          element={
            <RequireAuth requireRole={['admin']}>
              <PermissionsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/watched-paths"
          element={
            <RequireAuth requireRole={['admin']}>
              <WatchedPathsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/agents/:agentId/events"
          element={
            <RequireAuth>
              <AgentEventsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/agents/:agentId/devices"
          element={
            <RequireAuth>
              <AgentDevicesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/agents/:agentId/urls"
          element={
            <RequireAuth>
              <AgentUrlsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/wallboard"
          element={
            <RequireAuth>
              <WallboardPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <WindowManager />
      <UpdatesWindow />
      <MyProfileModal />
      <PermissionsModal />
      <WatchedPathsModal />
      <DeviceEventsToast />
      <AgentStatusToast />
      <AdminAlertToast />
    </AppShell>
  );
}

function EmptyDashboard(): JSX.Element {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        textAlign: 'center',
        p: 4,
      }}
    >
      <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
        Select an agent from the sidebar
      </Typography>
      <Typography variant="body2">
        The list of agents is in the left panel. Click an agent to open its dashboard with the available panels.
      </Typography>
    </Box>
  );
}
